export interface TypographyToken {
  size: string
  weight: number
  lineHeight: number
  family?: string
}

export interface DesignTokens {
  colors: Record<string, string>
  spacing: Record<string, string>
  typography: Record<string, TypographyToken>
  radius: Record<string, string> | string
  shadow: Record<string, string>
  fontFamily?: string
  fontWeightDefault?: number
  /**
   * Phase 393 WR-03: per-slot font stacks aligned with ThemeTokens.fonts.
   * Populated by lookupPreset → ThemeTokens.fonts and consumed by
   * applyTokensToCSSVars to emit --font-sans / --font-mono / --font-heading.
   */
  fonts?: { sans?: string; mono?: string; heading?: string }
  resolve(path: string): unknown
}

interface RawDesignTokens {
  colors: Record<string, string>
  spacing: Record<string, string>
  typography: Record<string, TypographyToken>
  radius: Record<string, string> | string
  shadow: Record<string, string>
  fontFamily?: string
  fontWeightDefault?: number
  fonts?: { sans?: string; mono?: string; heading?: string }
}

export function loadDesignTokens(raw: RawDesignTokens): DesignTokens {
  return {
    ...raw,
    resolve(path: string): unknown {
      const keys = path.split(".")
      let current: unknown = raw
      for (const key of keys) {
        if (current == null || typeof current !== "object") return undefined
        current = (current as Record<string, unknown>)[key]
      }
      return current
    },
  }
}

export function mergeTokens(
  base: RawDesignTokens,
  override: Partial<RawDesignTokens>,
): RawDesignTokens {
  return {
    colors: { ...base.colors, ...override.colors },
    spacing: { ...base.spacing, ...override.spacing },
    typography: { ...base.typography, ...override.typography },
    radius: override.radius !== undefined ? override.radius : base.radius,
    shadow: { ...base.shadow, ...override.shadow },
    fontFamily: override.fontFamily ?? base.fontFamily,
    fontWeightDefault: override.fontWeightDefault ?? base.fontWeightDefault,
    fonts:
      override.fonts || base.fonts
        ? { ...base.fonts, ...override.fonts }
        : undefined,
  }
}

// ---- Default theme constants (shadcn/ui) ----
// Complete set of CSS variable values so app content is fully isolated from
// the Studio shell theme. Values match globals.css :root / .dark blocks.

export const DEFAULT_LIGHT_THEME: Record<string, string> = {
  background: 'oklch(1 0 0)',
  foreground: 'oklch(0.145 0.004 285.823)',
  card: 'oklch(1 0 0)',
  'card-foreground': 'oklch(0.145 0.004 285.823)',
  popover: 'oklch(1 0 0)',
  'popover-foreground': 'oklch(0.145 0.004 285.823)',
  primary: 'oklch(0.488 0.243 264.376)',
  'primary-foreground': 'oklch(0.984 0.003 247.858)',
  secondary: 'oklch(0.967 0.003 264.542)',
  'secondary-foreground': 'oklch(0.21 0.006 285.885)',
  muted: 'oklch(0.967 0.003 264.542)',
  'muted-foreground': 'oklch(0.556 0.016 285.938)',
  accent: 'oklch(0.967 0.003 264.542)',
  'accent-foreground': 'oklch(0.21 0.006 285.885)',
  destructive: 'oklch(0.577 0.245 27.325)',
  'destructive-foreground': 'oklch(0.984 0.003 247.858)',
  border: 'oklch(0.922 0.004 286.032)',
  input: 'oklch(0.922 0.004 286.032)',
  ring: 'oklch(0.488 0.243 264.376)',
  'chart-1': 'oklch(0.845 0.143 164.978)',
  'chart-2': 'oklch(0.696 0.17 162.48)',
  'chart-3': 'oklch(0.596 0.145 163.225)',
  'chart-4': 'oklch(0.508 0.118 165.612)',
  'chart-5': 'oklch(0.432 0.095 166.913)',
}

export const DEFAULT_DARK_THEME: Record<string, string> = {
  background: 'oklch(0.145 0.004 285.823)',
  foreground: 'oklch(0.984 0.003 247.858)',
  card: 'oklch(0.145 0.004 285.823)',
  'card-foreground': 'oklch(0.984 0.003 247.858)',
  popover: 'oklch(0.145 0.004 285.823)',
  'popover-foreground': 'oklch(0.984 0.003 247.858)',
  primary: 'oklch(0.488 0.243 264.376)',
  'primary-foreground': 'oklch(0.984 0.003 247.858)',
  secondary: 'oklch(0.269 0.015 285.885)',
  'secondary-foreground': 'oklch(0.984 0.003 247.858)',
  muted: 'oklch(0.269 0.015 285.885)',
  'muted-foreground': 'oklch(0.708 0.014 285.823)',
  accent: 'oklch(0.269 0.015 285.885)',
  'accent-foreground': 'oklch(0.984 0.003 247.858)',
  destructive: 'oklch(0.396 0.141 25.723)',
  'destructive-foreground': 'oklch(0.984 0.003 247.858)',
  border: 'oklch(0.269 0.015 285.885)',
  input: 'oklch(0.269 0.015 285.885)',
  ring: 'oklch(0.488 0.243 264.376)',
  'chart-1': 'oklch(0.488 0.243 264.376)',
  'chart-2': 'oklch(0.696 0.17 162.48)',
  'chart-3': 'oklch(0.769 0.188 70.08)',
  'chart-4': 'oklch(0.627 0.265 303.9)',
  'chart-5': 'oklch(0.645 0.246 16.439)',
}

// ---- CSS custom property injection ----

/** Map from token camelCase color key → CSS variable name (without --) */
const COLOR_KEY_TO_CSS: Record<string, string> = {
  background: "background",
  foreground: "foreground",
  card: "card",
  cardForeground: "card-foreground",
  popover: "popover",
  popoverForeground: "popover-foreground",
  primary: "primary",
  primaryForeground: "primary-foreground",
  secondary: "secondary",
  secondaryForeground: "secondary-foreground",
  muted: "muted",
  mutedForeground: "muted-foreground",
  accent: "accent",
  accentForeground: "accent-foreground",
  destructive: "destructive",
  destructiveForeground: "destructive-foreground",
  border: "border",
  input: "input",
  ring: "ring",
  // Status tokens
  success: "success",
  successForeground: "success-foreground",
  warning: "warning",
  warningForeground: "warning-foreground",
  info: "info",
  infoForeground: "info-foreground",
  // Chart tokens (shadcn charts)
  chart1: "chart-1",
  chart2: "chart-2",
  chart3: "chart-3",
  chart4: "chart-4",
  chart5: "chart-5",
  // Sidebar tokens (shadcn sidebar)
  sidebarBackground: "sidebar-background",
  sidebarForeground: "sidebar-foreground",
  sidebarPrimary: "sidebar-primary",
  sidebarPrimaryForeground: "sidebar-primary-foreground",
  sidebarAccent: "sidebar-accent",
  sidebarAccentForeground: "sidebar-accent-foreground",
  sidebarBorder: "sidebar-border",
  sidebarRing: "sidebar-ring",
}

/** Check if a string is already in HSL channel format "H S% L%" */
const HSL_CHANNEL_RE = /^\d+(\.\d+)?\s+\d+(\.\d+)?%\s+\d+(\.\d+)?%$/
function isHslChannels(value: string): boolean {
  return HSL_CHANNEL_RE.test(value.trim())
}

/** Check if a string is an oklch() color value */
function isOklch(value: string): boolean {
  return value.trim().startsWith('oklch(')
}

/** Resolve a color value from tokens, trying both camelCase key and kebab-case CSS var name */
function resolveColorValue(colors: Record<string, string>, camelKey: string, cssVar: string): string | undefined {
  return colors[camelKey] ?? colors[cssVar]
}

/** Convert hex (#RRGGBB or #RGB) to HSL channel string "H S% L%" for Tailwind. */
function hexToHslChannels(hex: string): string | null {
  const cleaned = hex.replace("#", "")
  let r: number, g: number, b: number
  if (cleaned.length === 3) {
    r = parseInt(cleaned[0] + cleaned[0], 16) / 255
    g = parseInt(cleaned[1] + cleaned[1], 16) / 255
    b = parseInt(cleaned[2] + cleaned[2], 16) / 255
  } else if (cleaned.length === 6) {
    r = parseInt(cleaned.slice(0, 2), 16) / 255
    g = parseInt(cleaned.slice(2, 4), 16) / 255
    b = parseInt(cleaned.slice(4, 6), 16) / 255
  } else {
    return null
  }

  const max = Math.max(r, g, b)
  const min = Math.min(r, g, b)
  const l = (max + min) / 2
  let h = 0, s = 0

  if (max !== min) {
    const d = max - min
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min)
    if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) / 6
    else if (max === g) h = ((b - r) / d + 2) / 6
    else h = ((r - g) / d + 4) / 6
  }

  const hDeg = Math.round(h * 3600) / 10
  const sPct = Math.round(s * 1000) / 10
  const lPct = Math.round(l * 1000) / 10
  return `${hDeg} ${sPct}% ${lPct}%`
}

/**
 * Build a CSS variable map from design tokens, suitable for element-scoped
 * inline styles. Returns e.g. { '--primary': 'oklch(0.488 0.243 264.376)', ... }
 */
export function getTokenCSSVars(tokens: {
  colors?: Record<string, string>
  radius?: Record<string, string> | string
  fonts?: { sans?: string; mono?: string; heading?: string }
  fontFamily?: string
}): Record<string, string> {
  const vars: Record<string, string> = {}

  if (tokens.colors) {
    for (const [key, cssVar] of Object.entries(COLOR_KEY_TO_CSS)) {
      const raw = resolveColorValue(tokens.colors, key, cssVar)
      if (!raw) continue
      // oklch values are complete and used directly
      if (isOklch(raw)) {
        vars[`--${cssVar}`] = raw.trim()
        continue
      }
      const hsl = isHslChannels(raw) ? raw.trim() : hexToHslChannels(raw)
      if (hsl) vars[`--${cssVar}`] = `hsl(${hsl})`
    }
  }

  if (tokens.radius) {
    if (typeof tokens.radius === 'string') {
      vars['--radius'] = tokens.radius
    } else {
      const radiusLg = tokens.radius.lg ?? tokens.radius.md
      if (radiusLg) vars['--radius'] = radiusLg
    }
  }

  if (tokens.fontFamily) {
    vars['--cogo-font-family'] = tokens.fontFamily
  }

  // Phase 393: emit --font-sans / --font-mono / --font-heading when tokens.fonts
  // is provided. Partial fonts are allowed — only the supplied slots are emitted.
  if (tokens.fonts) {
    if (tokens.fonts.sans) vars['--font-sans'] = tokens.fonts.sans
    if (tokens.fonts.mono) vars['--font-mono'] = tokens.fonts.mono
    if (tokens.fonts.heading) vars['--font-heading'] = tokens.fonts.heading
  }

  return vars
}

/**
 * Build a complete React CSSProperties object for scoping design tokens
 * to a container element. Includes both `--primary` and `--color-primary`
 * so Tailwind v4 utility classes work even when tokens are set on a child
 * element instead of :root.
 */
export function getScopedTokenStyle(tokens: {
  colors?: Record<string, string>
  radius?: Record<string, string> | string
  fonts?: { sans?: string; mono?: string; heading?: string }
  fontFamily?: string
  fontWeightDefault?: number
}): Record<string, string> {
  const style: Record<string, string> = {}

  if (tokens.colors) {
    for (const [key, cssVar] of Object.entries(COLOR_KEY_TO_CSS)) {
      const raw = resolveColorValue(tokens.colors, key, cssVar)
      if (!raw) continue
      // oklch values are complete color values — set directly
      if (isOklch(raw)) {
        const val = raw.trim()
        style[`--${cssVar}`] = val
        style[`--color-${cssVar}`] = val
        continue
      }
      const hsl = isHslChannels(raw) ? raw.trim() : hexToHslChannels(raw)
      if (hsl) {
        // Tailwind v4 uses var(--primary) directly — value must be a complete CSS color.
        // Raw HSL channels "H S% L%" are NOT valid colors; wrap in hsl().
        const complete = `hsl(${hsl})`
        style[`--${cssVar}`] = complete
        style[`--color-${cssVar}`] = complete
      }
    }
  }

  if (tokens.radius) {
    if (typeof tokens.radius === 'string') {
      style['--radius'] = tokens.radius
    } else {
      const radiusLg = tokens.radius.lg ?? tokens.radius.md
      if (radiusLg) style['--radius'] = radiusLg
    }
  }

  if (tokens.fontFamily) {
    style['--cogo-font-family'] = tokens.fontFamily
    style['fontFamily'] = `${tokens.fontFamily}, sans-serif`
  }

  // Phase 393: emit --font-sans / --font-mono / --font-heading when tokens.fonts
  // is provided. Partial fonts are allowed — only the supplied slots are emitted.
  // When both tokens.fonts and the deprecated tokens.fontFamily are provided,
  // tokens.fonts wins for --font-sans (this block runs after the fontFamily block
  // and the new fonts API takes precedence per D-07).
  if (tokens.fonts) {
    if (tokens.fonts.sans) style['--font-sans'] = tokens.fonts.sans
    if (tokens.fonts.mono) style['--font-mono'] = tokens.fonts.mono
    if (tokens.fonts.heading) style['--font-heading'] = tokens.fonts.heading
  }

  if (tokens.fontWeightDefault) {
    style['fontWeight'] = String(tokens.fontWeightDefault)
  }

  return style
}

/**
 * Build a fully-isolated CSS style map for app content areas. Always returns a
 * complete set of CSS variables (never undefined), ensuring the app theme is
 * independent of the host shell (e.g. Studio dark/light toggle).
 *
 * 1. Start with a complete default theme (light or dark)
 * 2. Overlay any project-specific design tokens on top
 * 3. Set `colorScheme` for browser-level isolation (scrollbars, form controls)
 */
export function getIsolatedAppStyle(
  tokens?: {
    colors?: Record<string, string>
    radius?: Record<string, string> | string
    fonts?: { sans?: string; mono?: string; heading?: string }
    fontFamily?: string
    fontWeightDefault?: number
  },
  colorScheme: 'light' | 'dark' = 'light',
): Record<string, string> {
  const base = colorScheme === 'dark' ? DEFAULT_DARK_THEME : DEFAULT_LIGHT_THEME

  // Start with complete default theme — every semantic CSS var is defined
  const style: Record<string, string> = {}
  for (const [cssVar, value] of Object.entries(base)) {
    style[`--${cssVar}`] = value
    // oklch values are complete — use directly; legacy HSL channels need wrapping
    style[`--color-${cssVar}`] = isOklch(value) ? value : `hsl(${value})`
  }

  // Default radius — matches shadcn base theme (0.625rem)
  style['--radius'] = '0.625rem'

  // Overlay project tokens (if any)
  if (tokens) {
    const overrides = getScopedTokenStyle(tokens)
    Object.assign(style, overrides)
  }

  // Force color-scheme for browser-level isolation
  style['colorScheme'] = colorScheme

  return style
}

/**
 * Apply design tokens to CSS custom properties on a target element.
 * The target element is required — callers must provide an explicit element
 * to scope the tokens to. This prevents accidental CSS variable leaks into
 * the host document (e.g. Studio shell).
 */
export function applyTokensToCSSVars(tokens: DesignTokens, target: HTMLElement): void {
  const root = target

  // Apply color tokens as CSS custom properties
  if (!tokens.colors) return
  for (const [key, cssVar] of Object.entries(COLOR_KEY_TO_CSS)) {
    const raw = resolveColorValue(tokens.colors, key, cssVar)
    if (!raw) continue
    // oklch values are complete — set directly
    if (isOklch(raw)) {
      root.style.setProperty(`--${cssVar}`, raw.trim())
      continue
    }
    const hsl = isHslChannels(raw) ? raw.trim() : hexToHslChannels(raw)
    if (hsl) {
      root.style.setProperty(`--${cssVar}`, `hsl(${hsl})`)
    }
  }

  // Apply border radius
  if (tokens.radius) {
    if (typeof tokens.radius === 'string') {
      root.style.setProperty("--radius", tokens.radius)
    } else {
      const radiusLg = tokens.radius.lg ?? tokens.radius.md
      if (radiusLg) root.style.setProperty("--radius", radiusLg)
    }
  }

  // Apply font-family
  if (tokens.fontFamily) {
    root.style.setProperty("--cogo-font-family", tokens.fontFamily)
    root.style.fontFamily = `${tokens.fontFamily}, sans-serif`
  }

  // Phase 393 WR-03: apply per-slot font stacks as --font-sans / --font-mono
  // / --font-heading so callers that build a DesignTokens object with the
  // fonts field get the same CSS variables emitted by getIsolatedAppStyle.
  // Without this branch, applyTokensToCSSVars callers would silently miss
  // the font cascade despite DesignTokens carrying the data.
  if (tokens.fonts) {
    if (tokens.fonts.sans) root.style.setProperty("--font-sans", tokens.fonts.sans)
    if (tokens.fonts.mono) root.style.setProperty("--font-mono", tokens.fonts.mono)
    if (tokens.fonts.heading) root.style.setProperty("--font-heading", tokens.fonts.heading)
  }

  // Apply font-weight default
  if (tokens.fontWeightDefault) {
    root.style.fontWeight = String(tokens.fontWeightDefault)
  }
}
